import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../api/client';
import type { AdminUser } from '../types';

function errorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error ?? fallback;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return 'Never';
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? 'Never'
    : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function planBadge(u: AdminUser): { label: string; cls: string } {
  if (u.isTrial) {
    return u.trialExpired
      ? { label: 'Trial expired', cls: 'bg-red-100 text-red-700' }
      : { label: 'Trial', cls: 'bg-amber-100 text-amber-800' };
  }
  return { label: u.plan.charAt(0).toUpperCase() + u.plan.slice(1), cls: 'bg-emerald-100 text-emerald-800' };
}

// Owner-only. Lists accounts that signed up through Contractors.x; trial, pause
// and organisation controls are applied in InvoicePolish, which owns them.
export default function AdminPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [days, setDays] = useState<Record<string, string>>({});
  const [orgLimits, setOrgLimits] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await adminApi.users();
      if (!Array.isArray(data?.users)) {
        setUsers([]);
        setError('InvoicePolish returned an unexpected response. Its admin API may not be deployed yet.');
        return;
      }
      setUsers(data.users.map((u) => ({ ...u, organisations: Array.isArray(u.organisations) ? u.organisations : [], plan: u.plan ?? 'unknown' })));
    } catch (err) {
      setUsers([]);
      setError(errorMessage(err, 'Could not load users.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function run(key: string, action: () => Promise<unknown>, success: string) {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'That action failed.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contractor super</p>
      <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">Admin</h1>
      <p className="mt-1 text-sm text-slate-500">
        Accounts that signed up through Contractor Super, plus your own account. InvoicePolish-only users are not listed.
      </p>

      {error && <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {notice && <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{notice}</div>}

      {users === null ? (
        <p className="mt-6 text-sm text-slate-500">Loading users…</p>
      ) : users.length === 0 ? (
        <p className="mt-6 rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
          No Contractor Super sign-ups yet.
        </p>
      ) : (
        <div className="mt-6 space-y-4">
          <p className="text-xs text-slate-500">{users.length} user{users.length === 1 ? '' : 's'}</p>
          {users.map((u) => {
            const badge = planBadge(u);
            const d = days[u.id] ?? '7';
            const orgLimit = orgLimits[u.id] ?? String(u.trialMaxOrgs ?? 1);
            return (
              <div key={u.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-900">{u.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.cls}`}>{badge.label}</span>
                      {u.accountPaused && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">Paused</span>}
                      {!u.emailVerified && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">Email unverified</span>}
                    </div>
                    <div className="text-sm text-slate-600">{u.email}{u.company ? ` · ${u.company}` : ''}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      Joined {fmtDate(u.createdAt)} · Last sign-in {fmtDateTime(u.lastSignInAt)}
                      {u.lastSignInCity ? ` · ${u.lastSignInCity}${u.lastSignInCountryCode ? `, ${u.lastSignInCountryCode}` : ''}` : ''}
                    </div>
                    {u.isTrial && (
                      <div className="mt-1 text-xs text-slate-500">
                        {u.trialExpired ? 'Trial ended' : 'Trial ends'} {fmtDateTime(u.trialEndsAt)}
                        {u.trialMaxOrgs != null && ` · up to ${u.trialMaxOrgs} organisation${u.trialMaxOrgs === 1 ? '' : 's'}`}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {!u.managed && (
                      <span className="text-xs text-slate-500">InvoicePolish account — manage it in InvoicePolish admin</span>
                    )}
                    {u.managed && u.isTrial && (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={1}
                          max={90}
                          value={d}
                          onChange={(e) => setDays((prev) => ({ ...prev, [u.id]: e.target.value }))}
                          className="w-16 rounded-md border border-slate-300 px-2 py-1 text-xs"
                          aria-label="Days to extend trial"
                        />
                        <button
                          disabled={busy !== null}
                          onClick={() => run(`trial-${u.id}`, () => adminApi.extendTrial(u.id, Number(d)), `Trial extended by ${d} days for ${u.email}.`)}
                          className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Extend trial
                        </button>
                      </div>
                    )}
                    {u.managed && u.isTrial && (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={1}
                          max={50}
                          value={orgLimit}
                          onChange={(e) => setOrgLimits((prev) => ({ ...prev, [u.id]: e.target.value }))}
                          className="w-16 rounded-md border border-slate-300 px-2 py-1 text-xs"
                          aria-label="Organisations allowed during trial"
                        />
                        <button
                          disabled={busy !== null || Number(orgLimit) === u.trialMaxOrgs}
                          onClick={() => run(`orgs-${u.id}`, () => adminApi.setTrialOrgs(u.id, Number(orgLimit)), `${u.email} can now connect ${orgLimit} organisation${Number(orgLimit) === 1 ? '' : 's'} during their trial.`)}
                          className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Set org limit
                        </button>
                      </div>
                    )}
                    {u.managed && <button
                      disabled={busy !== null}
                      onClick={() => run(`pause-${u.id}`, () => adminApi.togglePause(u.id), u.accountPaused ? `${u.email} unpaused.` : `${u.email} paused.`)}
                      className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      {u.accountPaused ? 'Unpause' : 'Pause'}
                    </button>}
                    {u.managed && <button
                      disabled={busy !== null}
                      onClick={() => {
                        if (window.confirm(`Permanently delete ${u.email} and all their data? This also removes their InvoicePolish account.`)) {
                          void run(`del-${u.id}`, () => adminApi.deleteUser(u.id), `${u.email} deleted.`);
                        }
                      }}
                      className="rounded-md border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      Delete
                    </button>}
                  </div>
                </div>

                <div className="mt-3 border-t border-slate-100 pt-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Connected organisations</div>
                  {u.organisations.length === 0 ? (
                    <p className="mt-1 text-xs text-slate-500">None connected.</p>
                  ) : (
                    <ul className="mt-1 divide-y divide-slate-100">
                      {u.organisations.map((o) => (
                        <li key={o.connectionId} className="flex items-center justify-between gap-3 py-1.5 text-sm">
                          <span className="text-slate-800">
                            {o.tenantName}
                            <span className="ml-2 text-xs text-slate-500">connected {fmtDate(o.connectedAt)}</span>
                          </span>
                          {u.managed && <button
                            disabled={busy !== null}
                            onClick={() => {
                              if (window.confirm(`Disconnect "${o.tenantName}" for ${u.email}?`)) {
                                void run(`org-${o.connectionId}`, () => adminApi.disconnectOrg(u.id, o.connectionId), `Disconnected ${o.tenantName}.`);
                              }
                            }}
                            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          >
                            Disconnect
                          </button>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
